import React from 'react';
import PropTypes from 'prop-types';
import { getPlatformSpecs, getCharacterStatus } from '../../constants/platformSpecs';
import styles from './CharacterCounter.module.scss';

const CharacterCounter = ({
  charCount,
  platformType,
  showProgress = true,
  isThread = false,
  threadCount = 0
}) => {
  const specs = getPlatformSpecs(platformType);

  if (!specs) return null;

  const { characterLimit } = specs;

  // For threads, we don't show "over limit" error - it's expected
  const status = isThread ? 'thread' : getCharacterStatus(charCount, platformType);
  const percentage = isThread
    ? 100
    : Math.min((charCount / characterLimit) * 100, 100);
  const remaining = characterLimit - charCount;

  const getStatusMessage = () => {
    if (isThread) {
      return `Thread with ${threadCount} tweets`;
    }
    if (status === 'error') {
      return `${Math.abs(remaining)} characters over limit`;
    }
    if (status === 'warning') {
      return `${remaining} characters remaining`;
    }
    return null;
  };

  const statusMessage = getStatusMessage();

  return (
    <div className={styles.counterContainer}>
      {/* Warning message at top for threads */}
      {isThread && charCount > characterLimit && (
        <div className={styles.threadWarning}>
          Content exceeds {characterLimit} characters, will be split into a {threadCount}-tweet thread.
        </div>
      )}

      <div className={styles.counterInfo}>
        <span className={`${styles.count} ${isThread ? styles.thread : styles[status]}`}>
          {charCount.toLocaleString()} {isThread ? 'characters' : ''}
        </span>
        <span className={styles.separator}>/</span>
        <span className={styles.limit}>
          {isThread ? `${threadCount} tweets` : characterLimit.toLocaleString()}
        </span>
      </div>

      {showProgress && (
        <div className={styles.progressBar}>
          <div
            className={`${styles.progressFill} ${isThread ? styles.thread : styles[status]}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      )}

      {statusMessage && (
        <span className={`${styles.message} ${isThread ? styles.thread : styles[status]}`}>
          {statusMessage}
        </span>
      )}
    </div>
  );
};

CharacterCounter.propTypes = {
  charCount: PropTypes.number.isRequired,
  platformType: PropTypes.oneOf(['linkedin', 'facebook', 'x', 'instagram']).isRequired,
  showProgress: PropTypes.bool,
  isThread: PropTypes.bool,
  threadCount: PropTypes.number,
};

export default CharacterCounter;
